import {Button, Card, GridPhoto, PhotoGrid} from 'platform/components';
import {Box, HStack, Heading, Show, VStack} from 'platform/foundation';
import {Lightbox, useLightbox} from 'platform/lightbox';
import {useDateTimeFormatter} from 'platform/locale';

import {join, isNotEmpty, isNil} from 'ramda';

import {GetMetadaServiceCaseApiResponse} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';

import {parseDate, suffixTestId, composePath, TestIdProps, Nullish} from 'shared/web';

import {Video} from '../../VideoPreview/types/video';
import {getPrecedingPlayableVideos} from '../../VideoPreview/utils/getPrecedingPlayableVideos';
import {isVideoPlayable} from '../../VideoPreview/utils/isVideoPlayable';
import {VideoPreview} from '../../VideoPreview/VideoPreview';
import {useServiceCaseLightboxAssets} from '../hooks/useServiceCaseLightboxAssets';
import {ServiceCasePhotoAlbum} from '../types/ServiceCasePhotoAlbum';
import {ServiceCasePhotoTile} from '../types/ServiceCasePhotoTile';
import {ServiceCasePhotosAlbum} from './ServiceCasePhotosAlbum';

interface ServiceCasePhotosCardProps extends TestIdProps {
  serviceCase: GetMetadaServiceCaseApiResponse | Nullish;
  isExpanded: boolean;
  onExpandButtonClick: VoidFunction;
  albums: ServiceCasePhotoAlbum[];
  checkinPhotos: GridPhoto[];
  handoverPhotos: GridPhoto[];
  checkinVideos: Video[];
  handoverVideos: Video[];
  canDeletePhotos: boolean;
  processingPhotoIds: string[];
  onSetCoverPhoto: (photo: ServiceCasePhotoTile) => void;
  onUnsetCoverPhoto: (photo: ServiceCasePhotoTile) => void;
  onDeletePhoto: (photo: ServiceCasePhotoTile) => void;
  onDownloadAlbum: (albumId: string) => void;
  onDownloadCheckin: VoidFunction;
  onDownloadHandover: VoidFunction;
}

export function ServiceCasePhotosCard(props: ServiceCasePhotosCardProps) {
  const formatDateTime = useDateTimeFormatter();
  const {makeLightboxPhotos, makeLightboxVideos, photoDownloadSizeDialog} =
    useServiceCaseLightboxAssets();
  const serviceCaseId = props.serviceCase?.id ?? 'unknown';
  const [checkinPhotosLightboxControls, {onOpen: onCheckinPhotoOpen}] = useLightbox(
    `checkinPhotos-${serviceCaseId}`
  );
  const [handoverPhotosLightboxControls, {onOpen: onHandoverPhotoOpen}] = useLightbox(
    `handoverPhotos-${serviceCaseId}`
  );

  return (
    <Card
      title={join(' - ', [
        props.serviceCase?.createdAt &&
          formatDateTime('dateShort', parseDate(props.serviceCase?.createdAt)),
        props.serviceCase?.number,
      ])}
      variant="inlineWhite"
      isExpandable
      isExpanded={props.isExpanded}
      onExpandButtonClick={props.onExpandButtonClick}
      actions={[
        {
          type: 'button',
          variant: 'link',
          title: i18n.t('entity.workshop.actions.openServiceCase'),
          leftIcon: 'action/launch',
          isDisabled: isNil(props.serviceCase?.id),
          onClick: () =>
            window.open(
              composePath(workshopRoutes.serviceCaseDetail, {
                params: {
                  id: props.serviceCase?.id,
                },
              }),
              '_blank'
            ),
        },
      ]}
      data-testid={props['data-testid']}
    >
      <Show when={props.isExpanded}>
        <VStack spacing={4}>
          {props.albums.map((album) => (
            <ServiceCasePhotosAlbum
              key={album.id}
              data-testid={suffixTestId(`album-[${album.id}]`, props)}
              serviceCaseId={serviceCaseId}
              album={album}
              canDeletePhotos={props.canDeletePhotos}
              processingPhotoIds={props.processingPhotoIds}
              onSetCoverPhoto={props.onSetCoverPhoto}
              onUnsetCoverPhoto={props.onUnsetCoverPhoto}
              onDeletePhoto={props.onDeletePhoto}
              onDownload={props.onDownloadAlbum}
            />
          ))}
          <Show when={isNotEmpty(props.checkinPhotos)}>
            <HStack justify="space-between">
              <Heading size={4} alternative>
                {i18n.t('entity.workshop.labels.checkIn')}
              </Heading>
              <Button
                data-testid={suffixTestId('serviceCasePhotos-checkin-downloadAll', props)}
                variant="link"
                title={i18n.t('general.actions.downloadAll')}
                leftIcon="file/download"
                onClick={props.onDownloadCheckin}
                size="small"
              />
            </HStack>
            <PhotoGrid
              images={props.checkinPhotos}
              onSlideClick={(_, index) => onCheckinPhotoOpen(index)}
              data-testid={suffixTestId('checkinPhotos', props)}
            />
            <HStack wrap spacing={4}>
              {props.checkinVideos.map((video, index) => (
                <Box key={video.id} width={58} height={43}>
                  <VideoPreview
                    videoAsset={video}
                    isLoading={!isVideoPlayable(video)}
                    onOpenPreview={() =>
                      onCheckinPhotoOpen(
                        getPrecedingPlayableVideos(props.checkinVideos, index) +
                          props.checkinPhotos.length
                      )
                    }
                    data-testid={suffixTestId('checkinVideo', props)}
                  />
                </Box>
              ))}
            </HStack>
            <Lightbox
              data-testid={suffixTestId('checkinPhotos', props)}
              controls={checkinPhotosLightboxControls}
              images={makeLightboxPhotos(props.checkinPhotos)}
              videos={makeLightboxVideos(props.checkinVideos)}
            />
          </Show>
          <Show when={isNotEmpty(props.handoverPhotos)}>
            <HStack justify="space-between">
              <Heading size={4} alternative>
                {i18n.t('entity.workshop.labels.handover')}
              </Heading>
              <Button
                data-testid={suffixTestId('serviceCasePhotos-handover-downloadAll', props)}
                variant="link"
                title={i18n.t('general.actions.downloadAll')}
                leftIcon="file/download"
                onClick={props.onDownloadHandover}
                size="small"
              />
            </HStack>
            <PhotoGrid
              images={props.handoverPhotos}
              onSlideClick={(_, index) => onHandoverPhotoOpen(index)}
              data-testid={suffixTestId('handoverPhotos', props)}
            />
            <HStack wrap spacing={4}>
              {props.handoverVideos.map((video, index) => (
                <Box key={video.id} width={58} height={43}>
                  <VideoPreview
                    videoAsset={video}
                    isLoading={!isVideoPlayable(video)}
                    onOpenPreview={() =>
                      onHandoverPhotoOpen(
                        getPrecedingPlayableVideos(props.handoverVideos, index) +
                          props.handoverPhotos.length
                      )
                    }
                    data-testid={suffixTestId('handoverVideo', props)}
                  />
                </Box>
              ))}
            </HStack>
            <Lightbox
              data-testid={suffixTestId('handoverPhotos', props)}
              controls={handoverPhotosLightboxControls}
              images={makeLightboxPhotos(props.handoverPhotos)}
              videos={makeLightboxVideos(props.handoverVideos)}
            />
          </Show>
        </VStack>
      </Show>
      {photoDownloadSizeDialog}
    </Card>
  );
}
